import { createAsyncThunk } from '@reduxjs/toolkit'
import { get, post, put, remove } from '../services/crud'
import { ServiceConfig } from '../services/config'

import {
  ApiError,
  CreateOpportunityNoteBulkInput,
  CreateOpportunityNoteInput,
  GetOpportunityNotesInput,
  GetOpportunityNotesResponse,
} from './typings'
import { OpportunityNote } from '@user/components/Notes/typings'
import { toQueryString } from '@sales-center/common'

const baseUrl = `${ServiceConfig.baseAPIUrl}/OpportunityNote`

// GET OPPORTUNITY NOTES
export const getOpportunityNotes = createAsyncThunk<
  GetOpportunityNotesResponse,
  GetOpportunityNotesInput,
  { rejectValue: ApiError }
>('opportunityNotes/get', async (model, thunkApi) => {
  const response = await get(
    `${baseUrl}/opportunity/${
      model.opportunityId
    }?includeExternal=true&${toQueryString(model.query || '')}`,
  )

  if (response.ok) {
    const payload: GetOpportunityNotesResponse = await response.json()

    if (payload.items) {
      payload.items.forEach((note) => {
        note.createdDate = new Date(note.createdDate)
        note.rejectedDate = new Date(note.rejectedDate)
      })
    }

    payload.opportunityId = model.opportunityId

    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// GET OPPORTUNITY NOTE BY ID
export const getOpportunityNoteById = createAsyncThunk<
  OpportunityNote,
  number,
  { rejectValue: ApiError }
>('opportunityNote/getNoteById', async (id, thunkApi) => {
  const response = await get(`${baseUrl}/${id}`)

  if (response.ok) {
    const payload: OpportunityNote = await response.json()

    return {
      ...payload,
      createdDate: new Date(payload.createdDate),
      rejectedDate: new Date(payload.rejectedDate),
    }
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// DELETE OPPORTUNITY NOTE BY ID
export const deleteOpportunityNote = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError }
>('opportunityNote/delete', async (id, thunkApi) => {
  const response = await remove(`${baseUrl}/${id}`)

  if (response.ok) return id
  return thunkApi.rejectWithValue(await response.json())
})

// CREATE OPPORTUNITY NOTE
export const createOpportunityNote = createAsyncThunk<
  OpportunityNote,
  CreateOpportunityNoteInput,
  { rejectValue: ApiError }
>('opportunityNote/add', async (model, thunkApi) => {
  const response = await post(`${baseUrl}`, model)

  if (response.ok) {
    const payload: OpportunityNote = await response.json()

    return {
      ...payload,
      createdDate: new Date(payload.createdDate),
    }
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// UPDATE OPPORTUNITY NOTE
export const updateOpportunityNote = createAsyncThunk<
  OpportunityNote,
  CreateOpportunityNoteInput,
  { rejectValue: ApiError }
>('opportunityNote/update', async (model, thunkApi) => {
  const response = await put(`${baseUrl}`, model)

  if (response.ok) {
    const payload: OpportunityNote = await response.json()
    return {
      ...payload,
      createdDate: new Date(payload.createdDate),
    }
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})

// CREATE BULK OPPORTUNITY NOTE
export const createOpportunityNoteBulk = createAsyncThunk<
  { success: boolean },
  CreateOpportunityNoteBulkInput,
  { rejectValue: ApiError }
>('opportunityNote/bulkAdd', async (model, thunkApi) => {
  const response = await post(`${baseUrl}/bulkAdd`, model)

  if (response.ok) {
    return {
      success: true,
    }
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})
